// src/services/ecolabApi.js
// Servicio centralizado para el backend Economías Colaborativas

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5246';

async function get(path) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json();
}

async function post(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.mensaje || 'Error en el servidor'), { data, status: res.status });
  return data;
}

// ── Geografía (schema ecolab_main — sin autenticación) ───────────────────────

/** Trae países activos en Ecolab. Colombia PaisID=1045 */
export const getPaises = () => get('/api/ecolab/geo/paises');

/** Trae departamentos por país. Default 1045 = Colombia */
export const getDepartamentos = (paisId = 1045) =>
  get(`/api/ecolab/geo/departamentos?paisId=${paisId}`);

/** Trae municipios/ciudades por departamento */
export const getMunicipios = (departamentoId) =>
  get(`/api/ecolab/geo/municipios?departamentoId=${departamentoId}`);

// ── Validaciones previas ──────────────────────────────────────────────────────

/** Verifica si el email ya está registrado. Retorna { disponible: bool } */
export const verificarEmail = (email, cooperativaId) =>
  get(`/api/ecolab/registro-afiliacion/verificar-email?email=${encodeURIComponent(email)}${cooperativaId ? '&cooperativaId='+cooperativaId : ''}`);

/** Verifica si el documento ya existe en la cooperativa. Retorna { disponible: bool } */
export const verificarDocumento = (documento, cooperativaId) =>
  get(`/api/ecolab/registro-afiliacion/verificar-documento?documento=${documento}${cooperativaId ? `&cooperativaId=${cooperativaId}` : ''}`);

// ── Registro público de afiliación ──────────────────────────────────────────

/**
 * Envía la solicitud completa de afiliación.
 * @param {object} dto — ver RegistroAfiliacionDto en el backend
 * @returns { afiliadoID, usuarioID, email, estado, mensaje }
 */
export const registrarAfiliacion = (dto) =>
  post('/api/ecolab/registro-afiliacion', dto);

// ── Geocodificación para obtener código postal ───────────────────────────────

/**
 * Llama a Google Geocoding API para obtener el postal_code de una dirección.
 * Requiere REACT_APP_GOOGLE_MAPS_KEY en .env
 * @param {string} direccion
 * @param {string} ciudad  — ciudad para precisar la búsqueda
 * @returns {{ codigoPostal: string|null, lat: number|null, lng: number|null }}
 */
export async function geocodificarDireccion(direccion, ciudad = '', depto = '', pais = 'Colombia') {
  try {
    const params = new URLSearchParams({
      direccion: direccion || '',
      ciudad:    ciudad   || '',
      depto:     depto    || '',
      pais:      pais     || 'Colombia',
    });
    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5246';
    const res  = await fetch(`${BASE}/api/ecolab/geocoding/codigo-postal?${params}`);
    if (!res.ok) return { codigoPostal: null, lat: null, lng: null };
    return await res.json();
  } catch {
    return { codigoPostal: null, lat: null, lng: null };
  }
}

// ── Formulario de contacto ────────────────────────────────────────────────

/**
 * Envía un mensaje de contacto.
 * @param {{ nombre, email, telefono?, asunto?, mensaje }} dto
 */
export const enviarContacto = (dto) =>
  post('/api/ecolab/contacto', dto);

// ── Consulta de estado de afiliación ─────────────────────────────────────

/**
 * Consulta el estado de una solicitud por número de documento.
 * @param {string} documento
 */
export const consultarEstadoAfiliacion = (documento) =>
  get(`/api/ecolab/estado-afiliacion?documento=${encodeURIComponent(documento)}`);

// ── Verificación VISUAL de documento de identidad (Google Vision + ZXing) ──
// ── Cooperativas ─────────────────────────────────────────────────────────────
export const getCooperativasPublicas = () =>
  get('/api/ecolab/cooperativas/publicas');

// ── Pagos de inscripción ─────────────────────────────────────────────────────
export const getPagoConfigPublica  = () =>
  get('/api/ecolab/pago-inscripcion/config-publica');

export const iniciarPagoInscripcion = (afiliadoId) =>
  post(`/api/ecolab/pago-inscripcion/iniciar/${afiliadoId}`, {});

export const getEstadoPagoInscripcion = (afiliadoId) =>
  get(`/api/ecolab/pago-inscripcion/estado/${afiliadoId}`);

// ── Verificar imagen documento ───────────────────────────────────────────────
export async function verificarImagenDocumento(archivo, cedula = '', nombres = '', apellidos = '', tipoDocumento = 'cedula') {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5246';
  const formData = new FormData();
  formData.append('archivo',        archivo);
  formData.append('tipoDocumento',  tipoDocumento);
  if (cedula)    formData.append('cedula',    cedula);
  if (nombres)   formData.append('nombres',   nombres);
  if (apellidos) formData.append('apellidos', apellidos);
  const res = await fetch(`${BASE}/api/ecolab/verificar-documento`, {
    method: 'POST',
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.mensaje || 'Error verificando documento'), { data });
  return data;
}
